import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Divider, Grid, ListItemAvatar, ListItemText, Menu, MenuItem } from '@mui/material';
import { Avatar, IconGraphy, Tooltip, Typography, colors } from '@leapeasy/ui-kit';

import { setActiveNotificationAction } from 'store/actions/documentActions';
import { getNotificationBellDestination } from 'utils/notificationBellNavigation';
import { DEFAULT_PROFILE_AVATAR } from 'data/images/avatars';

export const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const docState = useSelector((state) => state.get('doc'));
  const activeNotifications = docState?.active_notification ?? [];

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleBellClick = (event) => {
    if (activeNotifications.length === 0) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationClick = useCallback(
    (notification) => {
      handleClose();
      dispatch(setActiveNotificationAction([]));
      navigate(getNotificationBellDestination(notification?.request_status));
    },
    [dispatch, handleClose, navigate],
  );

  const count = activeNotifications.length;

  return (
    <BellWrap>
      <Tooltip
        placement="bottom"
        showCarot={false}
        title={count > 0 ? `${count} new notification${count === 1 ? '' : 's'}` : 'Notifications'}
        variant="contained"
      >
        <BellButton
          type="button"
          aria-label="Notifications"
          aria-haspopup={count > 0 ? 'true' : undefined}
          onClick={handleBellClick}
        >
          <IconGraphy icon="Notifications.Notifications" width={20} height={20} forceColor />
          {count > 0 && (
            <CountBadge aria-label={`${count} unread`}>{count > 99 ? '99+' : count}</CountBadge>
          )}
        </BellButton>
      </Tooltip>

      {count > 0 && (
        <Menu
          id="menu-notification"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 320, maxWidth: 400 } }}
        >
          {activeNotifications.map((notification, index) => (
            <MenuItem
              key={notification?.request_id ?? `${notification?.requestor_email}-${index}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={2}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar
                      size="small"
                      iconImage={
                        <img
                          src={notification?.user_img || DEFAULT_PROFILE_AVATAR}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = DEFAULT_PROFILE_AVATAR;
                          }}
                        />
                      }
                    />
                  </ListItemAvatar>
                </Grid>
                <Grid item xs={10}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body3"
                        style={{ textTransform: 'uppercase', color: colors.purple[700] }}
                      >
                        {[notification?.request_type, notification?.request_status]
                          .filter(Boolean)
                          .join(' ')}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="small" style={{ color: colors.black[600] }}>
                        From: {notification?.requestor_email || '—'}
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </MenuItem>
          ))}
          <Divider variant="inset" component="li" />
        </Menu>
      )}
    </BellWrap>
  );
};

const BellWrap = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
`;

const BellButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: transparent;
  color: #3c393d;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    opacity: 0.9;
    background: ${colors.purple[100]};
  }
`;

const CountBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: ${colors.purple[700]};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  transform: translate(25%, -25%);
  box-sizing: border-box;
`;
